import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { Map as ImmutableMap, Record } from 'immutable';
import React, { useCallback } from 'react';

// App-Components
import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import { scheduledTransactionsActions, scheduledTransactionsTypes } from 'app-components/flux/scheduled-transactions';
import type { TransactionProps } from 'app-components/flux/transactions/transactionsTypes';
import { tracker } from 'app-components/utils/core';

import { billPresentmentSelectors } from 'app-components/flux/bill-presentment';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';

// MUI
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import ErrorIcon from '@mui/icons-material/Error';
import LinkIcon from '@mui/icons-material/Link';
import UnlinkIcon from '@mui/icons-material/LinkOff';
import OpenInBrowserIcon from '@mui/icons-material/OpenInBrowser';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import type { IconButtonProps } from '@mui/material';
import Typography from '@mui/material/Typography';

import { HandleDisconnectOption, HandleEndOption } from 'components/BillPresentment/utils';
// COMPONENTS
import { doBillPresentment } from 'components/Dialogs/BillPresentment/actions';
import { DIALOG_TYPE as DIALOG_CONFIRMATION, mkConfirmationDialogProps } from 'components/Dialogs/ConfirmationDialog';
import {
  DIALOG_TYPE as DIALOG_TYPE_SCHEDULED_TRANSACTION_EDIT,
  showScheduledTransactionEditDialog,
} from 'components/Dialogs/ScheduledTransactionEditDialog/actions';
import { showViewSeriesDialog } from 'components/Dialogs/ViewSeriesDialog/actions';
import { QButtonVariantEnum } from 'components/QButton';
import { QDropdownMenu, QMenuDivider, QMenuItem } from 'components/QDropdowns';
import useQDialogs from 'components/QDialogs/useQDialogs';

const location = 'settings recurring';

type RecurringMenuProps = {
  id?: string;
  model: scheduledTransactionsTypes.ScheduledTransaction;
  index: number;
  variant?: 'standard' | 'tax';
  slotProps?: { iconButton?: Partial<IconButtonProps> };
};

function RecurringMenu(props: RecurringMenuProps) {
  const { id, model, index, variant = 'standard', slotProps } = props;
  const dispatch = useAppDispatch();
  const { dialogAlert } = useQDialogs();
  const exist = Boolean(model.id);

  const connectOption = useAppSelector((state) => billPresentmentSelectors.getShowConnectOption(state, model));
  const isFeatureDrawer = Boolean(
    useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'webDrawerInput')),
  );

  const hasBPS = Boolean(model.presentmentAccountId);
  const isBillerConnecting = hasBPS && model.billerEnrollmentStatus === 'PENDING';
  const {
    size: iconButtonSize = 'large',
    shape: iconButtonShape = 'rounded',
    variant: iconButtonVariant,
    ...iconButtonRest
  } = slotProps?.iconButton ?? {};

  let billText;
  const reconnect =
    model?.presentmentAccountId &&
    (model.billerEnrollmentStatus === 'FAILED' || model.billerEnrollmentStatus === 'DISCONNECTED');
  if (reconnect) {
    billText = 'Reconnect biller';
  } else if (connectOption) {
    billText = 'Connect biller';
  }

  // ### -------------------------------------- ###
  // ###             Menu Functions             ###
  // ### -------------------------------------- ###
  const makeDialog = (data: any) => dispatch(rootUiActions.createDialog(rootUiTypes.mkRootUiData(data)));

  const confirmEndSeries = () => {
    makeDialog({
      id: 'end-series-confirmation-settings',
      type: DIALOG_CONFIRMATION,
      props: ImmutableMap(
        mkConfirmationDialogProps({
          title: 'End series',
          content: (
            <Typography variant='bodyRegular' id='confirmation-dialog-description'>
              Ending your&nbsp;<b>{model?.transaction?.payee || 'this transaction'}</b>&nbsp;series will only mark it as
              ended. It will not delete your series or any history related to it.
            </Typography>
          ),
          confirmLabel: 'End Series',
          confirmAction: (_reason, _closeEvent) => {
            HandleEndOption(dispatch, model, location, true);
          },
          denyLabel: 'Cancel',
        }),
      ),
    });
    tracker.track(tracker.events.recurringMenu, { action: 'endSeries', variant, context: id });
  };

  const handleAddSeries = () => {
    makeDialog({
      id: 'add-series-confirmation-settings',
      type: DIALOG_CONFIRMATION,
      props: ImmutableMap(
        mkConfirmationDialogProps({
          title: 'Add recurring series',
          content: `Adding the recurring series for ${model?.transaction?.payee} will add it to your schedule.`,
          confirmLabel: 'Add Series',
          confirmButtonVariant: QButtonVariantEnum.CONTAINED,
          confirmAction: (_reason, _closeEvent) => {
            dispatch(
              scheduledTransactionsActions.updateScheduledTransaction(
                scheduledTransactionsTypes.mkScheduledTransactionPatch({ id: model.id, isUserVerified: true }),
                { undo: { userMessage: 'Series added', disableUndoAction: true } },
              ),
            );
          },
          denyLabel: 'Cancel',
          denyButtonVariant: QButtonVariantEnum.DEFAULT,
        }),
      ),
    });
    tracker.track(tracker.events.recurringMenu, { action: 'addSeries', variant, context: id });
  };

  const confirmDeleteSeries = () => {
    makeDialog({
      id: 'delete-series-confirmation-settings',
      type: DIALOG_CONFIRMATION,
      props: ImmutableMap(
        mkConfirmationDialogProps({
          title: 'Delete recurring series',
          content: `Deleting the recurring series for ${model?.transaction?.payee} will delete all future reminders for this series.`,
          confirmLabel: 'Delete',
          confirmButtonVariant: QButtonVariantEnum.DELETE,
          confirmAction: (_reason, _closeEvent) => {
            dispatch(
              scheduledTransactionsActions.deleteScheduledTransaction(
                scheduledTransactionsTypes.mkScheduledTransactionPatch({ id: model.id, isDeleted: true }),
                { undo: { userMessage: 'Series deleted', disableUndoAction: true } },
              ),
            );
          },
          denyLabel: 'Cancel',
          denyButtonVariant: QButtonVariantEnum.DEFAULT,
        }),
      ),
    });
    tracker.track(tracker.events.recurringMenu, { action: 'deleteSeries', variant, context: id });
  };

  const handleViewSeries = () => {
    dispatch(
      showViewSeriesDialog({
        ...(Record.isRecord(model.transaction) ? model.transaction.toJS() : model.transaction),
        stModelId: model.id,
      } as TransactionProps),
    );
    tracker.track(tracker.events.recurringMenu, { action: 'viewSeries', variant, context: id });
  };

  const closeEditSeries = useCallback(() => {
    dispatch(rootUiActions.removeDialog(DIALOG_TYPE_SCHEDULED_TRANSACTION_EDIT, { animate: true }));
  }, [dispatch]);

  const closeEditCheck = (isDirty: boolean) => {
    if (!model?.id) {
      // scroll into view?
      // if (selectedRowRef) {
      //   setTimeout(() => {
      //     // we wait for the slide transition to finish before we scroll to the element
      //     if (selectedRowRef.scrollIntoView) {
      //       selectedRowRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
      //     } else if (selectedRowRef.scrollIntoViewIfNeeded) {
      //       selectedRowRef.scrollIntoViewIfNeeded(true);
      //     }
      //   }, 500)
      // }
    }

    if (isDirty) {
      dialogAlert(
        'Just checking...',
        'Are you sure you want to discard all changes?',
        (btnObj: { btnPressed: string }) => {
          if (btnObj.btnPressed === 'Discard') {
            closeEditSeries();
          }
        },
        ['Go Back', 'Discard'],
      );
    } else {
      closeEditSeries();
    }
  };

  const handleEditSeries = () => {
    dispatch(
      showScheduledTransactionEditDialog({
        scheduledTransaction: model,
        open: true,
        onClose: closeEditCheck,
        rootModalDrawer: isFeatureDrawer ? { width: 'Large' } : undefined,
      }),
    );
    tracker.track(tracker.events.recurringMenu, { action: 'editSeries', variant, context: id });
  };

  const handleConnectSeries = () => {
    // TODO - @LUC - if BVA, open edit series with bill-connect tab open
    const rootModalDrawer = isFeatureDrawer ? { width: 'Large' as const } : undefined;
    dispatch(doBillPresentment(model, location, rootModalDrawer));
    tracker.track(reconnect ? tracker.events.fixBillerStart : tracker.events.addBillerStart, { initiator: location });
    tracker.track(tracker.events.recurringMenu, { action: 'connectSeries', variant, context: id });
  };

  const unlink = () => {
    makeDialog({
      id: 'unlink-series-confirmation-settings',
      type: DIALOG_CONFIRMATION,
      props: ImmutableMap(
        mkConfirmationDialogProps({
          title: 'Disconnect Biller',
          content: `Your ${model?.transaction?.payee} 
            bill reminders will no longer be updated with the latest bill information. `,
          confirmLabel: 'Disconnect',
          confirmAction: (_reason, _closeEvent) => {
            HandleDisconnectOption(dispatch, model, location, true);
          },
          denyLabel: 'Cancel',
        }),
      ),
    });
    tracker.track(tracker.events.recurringMenu, { action: 'disconnectSeries', variant, context: id });
  };

  const addSeriesItem = (
    <QMenuItem
      key='add'
      variant='action'
      id='recurring-add-series'
      label='Add series'
      startIcon={<AddRoundedIcon fontSize='small' />}
      onClick={handleAddSeries}
    />
  );
  const viewSeriesItem = (
    <QMenuItem
      key='view'
      variant='action'
      id='recurring-view-series'
      label='View series'
      startIcon={<OpenInBrowserIcon fontSize='small' />}
      onClick={handleViewSeries}
    />
  );
  const editSeriesItem = (
    <QMenuItem
      key='edit'
      variant='action'
      id='recurring-edit-series'
      label='Edit series'
      startIcon={<EditRoundedIcon fontSize='small' />}
      onClick={handleEditSeries}
    />
  );
  const connectItems =
    !isBillerConnecting && variant !== 'tax' && billText
      ? [
          <QMenuItem
            key='connect'
            variant='action'
            id={reconnect ? 'recurring-reconnect-biller' : 'recurring-connect-biller'}
            label={billText}
            destructive={Boolean(reconnect)}
            startIcon={reconnect ? <ErrorIcon color='error' fontSize='small' /> : <LinkIcon fontSize='small' />}
            onClick={handleConnectSeries}
          />,
        ]
      : [];
  const disconnectItem = hasBPS
    ? [
        <QMenuItem
          key='disconnect'
          variant='action'
          id='recurring-disconnect-biller'
          label='Disconnect biller'
          startIcon={<UnlinkIcon fontSize='small' />}
          onClick={unlink}
        />,
      ]
    : [];
  const billerItems = [...connectItems, ...disconnectItem];
  const endSeriesItem = (
    <QMenuItem
      key='end'
      variant='action'
      id='recurring-end-series'
      label='End series'
      subtext='Keeps history, stops future reminders'
      startIcon={<StopRoundedIcon fontSize='small' />}
      onClick={confirmEndSeries}
    />
  );
  const deleteSeriesItem = (
    <QMenuItem
      key='delete'
      variant='action'
      id='recurring-delete-series'
      label='Delete series'
      subtext='Permanently removes the series & reminders'
      destructive
      startIcon={<DeleteRoundedIcon fontSize='small' />}
      onClick={confirmDeleteSeries}
    />
  );
  const destructiveItems = [...(variant !== 'tax' ? [endSeriesItem] : []), deleteSeriesItem];

  let menuItems: React.ReactNode[];
  if (!model.isUserVerified) {
    menuItems = [addSeriesItem, editSeriesItem, <QMenuDivider key='unverified-divider' />, deleteSeriesItem];
  } else if (model?.isCompleted) {
    menuItems = [viewSeriesItem, <QMenuDivider key='completed-divider' />, deleteSeriesItem];
  } else {
    menuItems = [
      viewSeriesItem,
      editSeriesItem,
      ...(billerItems.length > 0 ? [<QMenuDivider key='biller-divider' />, ...billerItems] : []),
      ...(destructiveItems.length > 0 ? [<QMenuDivider key='destructive-divider' />, ...destructiveItems] : []),
    ];
  }

  return (
    <div
      role='presentation'
      onClick={(event) => {
        event.stopPropagation();
      }}
    >
      <QDropdownMenu
        id={id ?? `scheduled-txn-${index}-menu`}
        aria-label='Series options'
        disabled={!exist}
        iconButtonSize={iconButtonSize}
        iconButtonShape={iconButtonShape}
        iconButtonVariant={iconButtonVariant}
        IconButtonProps={iconButtonRest}
        width={320}
      >
        {menuItems}
      </QDropdownMenu>
    </div>
  );
}

RecurringMenu.displayName = 'RecurringMenu';

export default RecurringMenu;
